import { useMeFavouritePartner } from '@capsicum/common';
import {
	useFollowedPartners,
	useGetFanTokenStakeOperationByCurrencyIds,
	usePartners,
	useWalletsBalanceWrapper
} from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import { DEFAULT_FEED_DATA_VALUE, feedDataAtom, type FeedDataAtomType } from '../data-access';

import { useParticipationRequirement } from './useParticipationRequirement';

type UseSetFeedDataAtomProps = { enabled: boolean; partnerId?: null | string };

/**
 * This hook is used to set the "feedDataAtom" on main pages.
 * Mainly used for avoiding multiple queries to being re-rendered on every Post component.
 * TODO: Refactor this logic once FanZone is shut down
 */
export const useSetFeedDataAtom = (
	{ enabled, partnerId }: UseSetFeedDataAtomProps = { enabled: true, partnerId: null }
): FeedDataAtomType => {
	const { getPartnerBy, partners } = usePartners();
	const setFeedDataAtom = useSetAtom(feedDataAtom);
	const walletsBalance = useWalletsBalanceWrapper({ isStakable: true });
	const { favoritePartner } = useMeFavouritePartner({ enabled });
	const { followedPartners } = useFollowedPartners(favoritePartner?.id);
	const { requirement: participationRequirement } = useParticipationRequirement();
	const followedCurrencyPartnerIds = (followedPartners?.map(partner => partner.currencyId) || []) as Array<string>;
	const partnerPageCurrencyId = useMemo(
		() => getPartnerBy(p => p.id === partnerId)?.currencyId,
		[getPartnerBy, partnerId]
	);
	const fanZoneCurrencyIds = favoritePartner?.currencyId
		? [favoritePartner.currencyId, ...followedCurrencyPartnerIds]
		: followedCurrencyPartnerIds;

	const { getOperationDetails, isOperationDetailsLoading } = useGetFanTokenStakeOperationByCurrencyIds({
		currencyIds: partnerPageCurrencyId ? [partnerPageCurrencyId, ...fanZoneCurrencyIds] : fanZoneCurrencyIds
	});

	const isWalletsBalanceLoading = walletsBalance?.isLoading ?? false;

	const feed = useMemo(() => {
		if (!enabled) return;

		const data = {
			favouritePartnerId: favoritePartner?.id,
			followedPartners: followedPartners,
			getOperationDetailsFromPartner: getOperationDetails,
			isFeedAtomDataLoading: isOperationDetailsLoading || isWalletsBalanceLoading,
			participationRequirement,
			partnersData: { getPartnerBy, partners },
			walletsBalance: walletsBalance
		};

		return data;
	}, [
		enabled,
		partners,
		getPartnerBy,
		walletsBalance,
		followedPartners,
		favoritePartner?.id,
		participationRequirement,
		getOperationDetails,
		isOperationDetailsLoading
	]);

	useEffect(() => {
		feed && setFeedDataAtom(feed);
	}, [feed]);

	return feed ?? DEFAULT_FEED_DATA_VALUE;
};
